import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Store, Package, CheckSquare, ShieldCheck,
  BarChart3, Bell, Settings, ChevronDown, ChevronRight,
  Layers, X
} from 'lucide-react';
import logoImg from '../../logo.png';

const nav = [
  { path: '/', label: 'Дашборд', icon: LayoutDashboard },
  {
    label: 'Сотрудники', icon: Users, children: [
      { path: '/employees', label: 'Список сотрудников' },
      { path: '/employees/actions', label: 'Журнал действий' },
    ]
  },
  {
    label: 'Магазины', icon: Store, children: [
      { path: '/stores', label: 'Список магазинов' },
    ]
  },
  {
    label: 'Товары', icon: Package, children: [
      { path: '/products', label: 'Каталог товаров' },
      { path: '/products/categories', label: 'Категории / Иерархия' },
      { path: '/products/expiry', label: 'Сроки годности (ОСГ)' },
    ]
  },
  {
    label: 'Задачи', icon: CheckSquare, children: [
      { path: '/tasks', label: 'Все задачи' },
      { path: '/tasks/create', label: 'Создать задачу' },
      { path: '/tasks/templates', label: 'Шаблоны задач' },
    ]
  },
  {
    label: 'ОСГ Проверки', icon: ShieldCheck, children: [
      { path: '/osg/checks', label: 'Журнал проверок' },
      { path: '/osg/critical', label: 'Критические сроки' },
    ]
  },
  {
    label: 'Выкладка', icon: Layers, children: [
      { path: '/display/reports', label: 'Журнал выкладок' },
      { path: '/display/photos', label: 'Фото-отчёты' },
    ]
  },
  {
    label: 'Аналитика', icon: BarChart3, children: [
      { path: '/analytics', label: 'Сводка' },
      { path: '/analytics/employees', label: 'По сотрудникам' },
      { path: '/analytics/stores', label: 'По магазинам' },
      { path: '/analytics/categories', label: 'По категориям' },
    ]
  },
  { path: '/notifications', label: 'Уведомления', icon: Bell },
  {
    label: 'Настройки', icon: Settings, children: [
      { path: '/settings/roles', label: 'Роли и права' },
      { path: '/settings', label: 'Общие настройки' },
      { path: '/settings/audit', label: 'Журнал событий' },
    ]
  },
];

function NavItem({ item, depth = 0, onNavigate }) {
  const location = useLocation();
  const isChildActive = item.children?.some(c =>
    location.pathname === c.path || location.pathname.startsWith(c.path + '/')
  );
  const [open, setOpen] = useState(isChildActive);
  const Icon = item.icon;

  if (item.children) {
    return (
      <div>
        <button onClick={() => setOpen(o => !o)} style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '10px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
          background: isChildActive ? 'rgba(0, 206, 238, 0.08)' : 'transparent',
          color: isChildActive ? '#009995' : '#475569',
          transition: 'all 0.15s ease',
        }}
          onMouseEnter={e => { if (!isChildActive) e.currentTarget.style.background = '#F1F5F9'; }}
          onMouseLeave={e => { if (!isChildActive) e.currentTarget.style.background = 'transparent'; }}>
          {Icon && <Icon size={18} strokeWidth={isChildActive ? 2.2 : 1.8} />}
          <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
          {open ? <ChevronDown size={14} style={{ opacity: 0.5 }} /> : <ChevronRight size={14} style={{ opacity: 0.5 }} />}
        </button>
        <div style={{
          overflow: 'hidden',
          maxHeight: open ? 400 : 0,
          transition: 'max-height 0.25s ease',
        }}>
          <div style={{
            marginLeft: 20,
            borderLeft: '2px solid #E2E8F0',
            paddingLeft: 10,
            marginTop: 2,
            marginBottom: 4,
          }}>
            {item.children.map(c => (
              <NavLink key={c.path} to={c.path} onClick={onNavigate} style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                borderRadius: 8, fontSize: 12.5, fontWeight: isActive ? 600 : 400,
                color: isActive ? '#009995' : '#64748B',
                background: isActive ? 'rgba(0, 206, 238, 0.06)' : 'transparent',
                textDecoration: 'none', transition: 'all 0.12s',
                marginBottom: 1,
              })}>
                <span style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: 'currentColor', flexShrink: 0, opacity: 0.6,
                }} />
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <NavLink to={item.path} onClick={onNavigate} style={({ isActive }) => ({
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
      borderRadius: 10, fontSize: 13, fontWeight: isActive ? 600 : 500,
      color: isActive ? '#009995' : '#475569',
      background: isActive ? 'rgba(0, 206, 238, 0.08)' : 'transparent',
      textDecoration: 'none', transition: 'all 0.15s',
    })}>
      {Icon && <Icon size={18} strokeWidth={1.8} />}
      {item.label}
    </NavLink>
  );
}

export default function Sidebar({ collapsed, onClose, isMobile }) {
  return (
    <>
      {/* Mobile overlay */}
      {isMobile && !collapsed && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)',
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            zIndex: 199, animation: 'fadeIn 0.15s ease',
          }}
        />
      )}

      <aside style={{
        width: isMobile ? 280 : (collapsed ? 0 : 260),
        minWidth: isMobile ? (collapsed ? 0 : 280) : (collapsed ? 0 : 260),
        height: '100vh',
        background: '#fff',
        borderRight: '1px solid #E2E8F0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: isMobile ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'width 0.25s ease, min-width 0.25s ease',
        position: isMobile ? 'fixed' : 'sticky',
        top: 0,
        left: 0,
        zIndex: isMobile ? 200 : 10,
        transform: isMobile ? (collapsed ? 'translateX(-100%)' : 'translateX(0)') : 'none',
        boxShadow: isMobile && !collapsed ? '4px 0 30px rgba(0,0,0,0.1)' : 'none',
      }}>
        {/* Logo */}
        <div style={{
          padding: '0 16px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid #F1F5F9', flexShrink: 0,
        }}>
          <img
            src={logoImg}
            alt="PDA Logo"
            style={{ height: 34, maxWidth: 170, objectFit: 'contain' }}
          />
          {isMobile && (
            <button onClick={onClose} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 8, background: '#F1F5F9',
              color: '#64748B', cursor: 'pointer',
            }}>
              <X size={16} />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav style={{
          flex: 1, overflowY: 'auto', padding: '12px 10px',
          display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          {nav.map((item, i) => (
            <NavItem key={i} item={item} onNavigate={isMobile ? onClose : undefined} />
          ))}
        </nav>

        {/* Footer */}
        <div style={{
          padding: '14px 16px', borderTop: '1px solid #F1F5F9',
          fontSize: 11, color: '#94A3B8', textAlign: 'center',
          fontWeight: 500,
        }}>
          PDA v2.0 &middot; Control Panel
        </div>
      </aside>
    </>
  );
}
